import { fixedNumber } from '@utils';

/**
 * Trims leading and trailing whitespace from each line in a given string.
 *
 * @param s - The input string to be trimmed.
 *
 * @returns The input string with leading and trailing whitespace removed from each line.
 */
export function trimLines(s: string): string {
  return s
    .split('\n')
    .map(line => line.trim())
    .join('\n');
}

/**
 * Surrounds a given string with specified left and right strings.
 *
 * @param s - The string to be flanked.
 * @param left - The string to be added to the left of `s`.
 * @param right - The string to be added to the right of `s`.
 * @returns The resulting string with `left` and `right` flanking `s`.
 */
export function flanked(s: string, left: string, right: string): string {
  return left + s + right;
}

/**
 * Converts a given string to title case.
 *
 * The function first converts the entire string to lowercase, then splits it into words,
 * capitalizes the first letter of each word, and finally joins the words back into a single string.
 *
 * @param str - The input string to be converted to title case.
 * @returns The input string converted to title case.
 */
export function toTitleCase(str: string): string {
  return str
    .toLowerCase() // Convert the entire string to lowercase first
    .split(' ') // Split the string into words
    .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter of each word
    .join(' '); // Join the words back into a single string
}

/**
 * Converts a non-negative integer to a string representing its corresponding
 * letter code in a manner similar to Excel column naming.
 *
 * @param n - The non-negative integer to convert.
 * @returns The letter code corresponding to the given integer.
 * @throws Will throw an error if the input number is negative.
 *
 * @example
 * ```typescript
 * letterCode(0); // Returns 'A'
 * letterCode(25); // Returns 'Z'
 * letterCode(26); // Returns 'AA'
 * letterCode(51); // Returns 'AZ'
 * letterCode(52); // Returns 'BA'
 * ```
 */
export function letterCode(n: number): string {
  if (n < 0) {
    throw new Error('n must be non-negative.');
  }
  let result = '';
  do {
    const digit = n % 26;
    result = String.fromCharCode(65 + digit) + result;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return result;
}

export function composeStruct(
  fields: [string, string | number | boolean | undefined][],
  fractionDigits?: number
): string {
  const newLocal = fields
    .flatMap(([name, value]) => {
      let formattedValue: string;

      if (value === undefined) {
        return;
      } else if (typeof value === 'number')
        formattedValue = fixedNumber(value, fractionDigits);
      else formattedValue = value.toString();

      return `${name}: ${formattedValue}`;
    })
    .join(', ');
  return flanked(newLocal, '{', '}');
}

export function composeArray(
  values: (string | number)[],
  fractionDigits?: number
): string {
  const newLocal = values
    .map(value => {
      let formattedValue = value;
      if (typeof value === 'number') {
        formattedValue = fixedNumber(value, fractionDigits);
      }
      return formattedValue;
    })
    .join(', ');
  return flanked(newLocal, '[', ']');
}

/**
 * Converts a value of any type to a string representation.
 *
 * @param value - The value to convert to a string
 * - If the value is already a string, it is returned as-is
 * - If the value is null or undefined, an empty string is returned
 * - Otherwise, the value's toString() method is called
 *
 * @returns A string representation of the input value
 */
export function stringFromOptional(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (value === undefined || value === null) {
    return '';
  }
  // eslint-disable-next-line @typescript-eslint/no-base-to-string
  return value.toString();
}

/**
 * Converts an empty string to undefined, otherwise returns the string as is.
 *
 * @param value - The string to check
 * @returns The original string, or undefined if the input was an empty string
 */
export function optionalFromString(value: string): string | undefined {
  // If the value is an empty string, return undefined.
  return value === '' ? undefined : value;
}

export function interpolateString(
  template: string,
  placeholders: Record<string, string> = {}
): string {
  return template.replace(/\$\{(\w+)\}/g, (_, key: string) => {
    if (key in placeholders) {
      return placeholders[key];
    } else {
      console.warn(`Missing placeholder value for "${key}" in template: "${template}"`);
      return `<${key}>`;
    }
  });
}
